import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { BroadcastRecord } from '@/types'

interface BroadcastAccount {
  igUserId: string
  pageId: string
  name: string
  fbOnly: boolean
  igLogin: boolean
  totalContacts: number
  within24h: number
  recentFail: boolean
}

interface BroadcastProfile {
  id: string
  name: string
  pageIds: string[]
}

interface BroadcastConfig {
  accounts: BroadcastAccount[]
  profiles?: BroadcastProfile[]
  lastSync?: string | null
}

interface BroadcastButton {
  text: string
  type: 'url' | 'postback'
  value: string
  payload?: string
}

interface BroadcastFlowStep {
  message: string
  imageUrl?: string | null
  buttons?: BroadcastButton[]
}

interface BroadcastPayload {
  igUserId?: string
  igUserIds?: string[]
  filter: 'all' | '24h' | 'tag'
  filterTag?: string
  message?: string
  imageUrl?: string
  r?: number
  buttonText?: string
  buttonUrl?: string
  buttons?: BroadcastButton[]
  flowSteps?: BroadcastFlowStep[]
  scheduledAt?: string
  repeatType?: string
  repeatUntil?: string
}

interface BroadcastResult {
  ok: boolean
  sent: number
  failed: number
  skipped: number
  tagged: number
}

export function useBroadcastConfig() {
  return useQuery<BroadcastConfig>({
    queryKey: ['broadcast-config'],
    queryFn: () => api.get('/broadcast/config'),
  })
}

export function useSendBroadcast() {
  const qc = useQueryClient()
  return useMutation<BroadcastResult, Error, BroadcastPayload>({
    mutationFn: (data) => api.post('/broadcast/send', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-history'] }),
  })
}

export function useBroadcastHistory() {
  return useQuery<BroadcastRecord[]>({
    queryKey: ['broadcast-history'],
    queryFn: () => api.get('/broadcast/history'),
  })
}

export function useDeleteBroadcast() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/broadcast/history/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-history'] }),
  })
}

export function useClearBroadcastHistory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/broadcast/history/all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-history'] }),
  })
}

// Broadcast Schedules
export function useBroadcastSchedules() {
  return useQuery({ queryKey: ['broadcast-schedules'], queryFn: () => api.get('/broadcast/schedules') })
}
export function useCreateSchedule() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (data: any) => api.post('/broadcast/schedules', data), onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-schedules'] }) })
}
export function useUpdateSchedule() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: ({ id, data }: { id: string; data: any }) => api.put(`/broadcast/schedules/${id}`, data), onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-schedules'] }) })
}
export function useDeleteSchedule() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => api.delete(`/broadcast/schedules/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-schedules'] }) })
}
export function useClearSchedules() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: () => api.delete('/broadcast/schedules/all'), onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-schedules'] }) })
}
export function useToggleSchedule() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => api.patch(`/broadcast/schedules/${id}/toggle`), onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-schedules'] }) })
}

// Broadcast Campaigns
export interface BroadcastCampaign {
  id: string
  name: string
  whatsappLink: string
  phrases: string[]
  subtitles: string[]
  presetIds: string[]
  images: string[]
  buttonTexts: string[]
  accountIds: string[] | null
  timesOfDay: string[]
  daysOfWeek: number[]
  isActive: boolean
  phraseIndex: number
  imageIndex: number
  buttonIndex: number
  lastRunAt: string | null
  createdAt: string
}

export function useBroadcastCampaigns() {
  return useQuery<BroadcastCampaign[]>({
    queryKey: ['broadcast-campaigns'],
    queryFn: () => api.get('/broadcast/campaigns'),
  })
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<BroadcastCampaign, 'id' | 'isActive' | 'phraseIndex' | 'imageIndex' | 'buttonIndex' | 'lastRunAt' | 'createdAt'>) =>
      api.post('/broadcast/campaigns', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] }),
  })
}

export function useUpdateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<BroadcastCampaign> & { id: string }) =>
      api.put(`/broadcast/campaigns/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] }),
  })
}

export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/broadcast/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] }),
  })
}

export function useToggleCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.patch(`/broadcast/campaigns/${id}/toggle`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] }),
  })
}

// ─── Message Templates (Meta utility templates p/ fora da janela de 24h) ───

export interface BroadcastTemplateItem {
  id: number
  name: string
  body: string
  var1: string | null
  var2: string | null
  buttonText: string | null
  buttonUrl: string | null
  category: string
  isRecovery?: boolean
  recoverySent?: number
  recoveryClicks?: number
  approved: number
  rejected: number
  pending: number
  totalPages: number
  reasons: Record<string, number> | null
  createdAt: number
}

export function useTemplates() {
  return useQuery<BroadcastTemplateItem[]>({
    queryKey: ['broadcast-templates'],
    queryFn: () => api.get('/broadcast/templates'),
  })
}

export function useCreateTemplate() {
  const qc = useQueryClient()
  return useMutation<
    { ok: boolean; name: string; approved: number; rejected: number; pending: number; failed: number; total: number; reasons: Record<string, number> },
    Error,
    { name: string; body: string; var1?: string; var2?: string; buttonText?: string; buttonUrl?: string; buttons?: Array<Record<string, string>>; example?: string; example2?: string; imageUrl?: string; headerTitle?: string; igUserIds?: string[]; isRecovery?: boolean }
  >({
    mutationFn: (data) => api.post('/broadcast/templates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-templates'] }),
  })
}

// Reenvia o template às páginas que ainda não o têm (consulta a Meta página por página)
export function useRetryTemplate() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; name: string; created: number; approved: number; rejected: number; pending: number; failed: number; total: number; reasons: Record<string, number> }, Error, number>({
    mutationFn: (id) => api.post(`/broadcast/templates/${id}/retry`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-templates'] }),
  })
}

export function useDeleteTemplate() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; deleted: number }, Error, string>({
    mutationFn: (name) => api.delete('/broadcast/templates', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-templates'] }),
  })
}
